import {computed, ref} from "@vue/composition-api";
import {Web, URI} from "sip.js";

const {
  SimpleUser,
  simpleUserDelegate,
  RegistererUnregisterOptions,
  Inviter,
  UserAgent,
} = Web;
import store from "@/store";
import axios from "@axios";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import {useToast} from "vue-toastification/composition";
import {EventBus} from "@/libs/event-bus";
import pbx_call from "@/libs/i18n/sections/en/pbx_call";

export default function useOmnicloud(device) {
  const isOnCall = computed(() => store.state.sip.isOnCall);
  const isCalling = computed(() => store.state.sip.isCalling);
  const isHolded = computed(() => store.state.sip.isHolded);
  const isRinging = computed(() => store.state.sip.isRinging);
  const isRingingLocal = ref(false);
  const sessionID = ref("");

  const isConnected = ref("Disconnected");
  const isMuted = ref(false);
  const intervalRefresh = ref(true); //To know if it should try to connect in the interval
  let intervalId = null; // To not repeat the interval, and create many intervals.
  const callingNumber = ref("");
  const callRecieved = ref({callingPartyNumber: ""});
  const lockAnswerCall = ref(false);
  const toast = useToast();
  const isRegistered = store.state.sip.isRegistered;
  const presavedAlarm = computed(() => {
    return store.state.sip.presavedAlarm;
  });

  const omnicloudAlarm = computed(() => {
    return store.state.sip.omnicloudAlarm;
  });

  const shouldDisconnect = ref(true);

  //Cronometer for calls
  const cronometerRef = ref(null);
  const timeCall = computed(() => store.state.sip.timeCall);
  const presavedCaller = computed(() => store.state.sip.presavedCaller);
  let pbxManagerUrl = "";
  let simpleUser;

  const fetchQueue = () => store.dispatch("pbx_calls/getIncoming", device.domain);
  const setCurrentCall = (eventData) => {
    if (!eventData) {
      return;
    }
    const {type, sessionId, details } = eventData;

    if (type === "CHANNEL_CREATE") {
      const _eventData = {
        sessionId,
        from: details.caller_orig_number,
        to: details.caller_dest_number,
        domain: details.domain_name,
        pbxManagerUrl: details.master_account_api_url,
      };
      store.dispatch("pbx_calls/setCurrentCall", _eventData);
    }

    if (type === "CHANNEL_STATE") {
      const _eventData = {
        from: details.caller_orig_number,
        to: details.caller_dest_number,
      };
      store.dispatch("pbx_calls/updateCurrentCall", _eventData);
    }

    if (type === "CHANNEL_HANGUP") {
      store.dispatch("pbx_calls/resetCurrentCall");
    }
  };

  setTimeout(async () => {
    window.Echo.private('pbx-manager-events')
      .listen(".CHANNEL_CREATE", (data) => {
        setCurrentCall(data);
        fetchQueue();
      })
      .listen(".CHANNEL_STATE", (data) => {
        setCurrentCall(data);
      })
      .listen(".CHANNEL_ANSWER", (data) => {
        setCurrentCall(data);
        fetchQueue();
        if (omnicloudAlarm.value !== "") {
          axios.post("/v1/pbx_calls/update-alarm", {
            id_alarm: omnicloudAlarm.value,
            id_session: data.sessionId
          });
        }
      })
      .listen(".CHANNEL_HANGUP", (data) => {
        setCurrentCall(data);
        fetchQueue();
      })
  }, 1500);

  const simpleUserDelegate = {
    onCallCreated: () => {
      store.dispatch("sip/setIsRinging", false);
      isRingingLocal.value = false;
      store.dispatch("sip/setIsCalling", false);
      store.dispatch("sip/setIsOnCall", true);
      store.dispatch("sip/setActiveSessionProvider", "omnicloud");
      store.dispatch("sip/setIconColor", "orange");
    },
    onCallAnswered: () => {
      store.dispatch("sip/setIsRinging", false);
      isRingingLocal.value = false;
      store.dispatch("sip/setIsCalling", false);
      store.dispatch("sip/setIsOnCall", true);
      store.dispatch("sip/setIconColor", "orange");
      startCronometer();
    },
    onCallHangup: () => {
      store.dispatch("sip/setIsRinging", false);
      isRingingLocal.value = false;
      store.dispatch("sip/setIsCalling", false);
      store.dispatch("sip/setIsOnCall", false);
      store.dispatch("sip/setActiveSessionProvider", "");
      store.dispatch("sip/setIconColor", "white");

      if (presavedAlarm.value && Object.keys(presavedAlarm.value).length > 0){
        store.dispatch("pbx_calls/updateStatusAlarm", {
          call_uuid: sessionID.value,
        });
      }

      sessionID.value = "";
      store.dispatch("sip/resetPresavedCaller");
      store.dispatch("sip/resetPresavedAlarm");
      if (simpleUser.session) hangUp();
      stopCronometer();
      store.dispatch("sip/setOmnicloudAlarm", "");
    },
    onCallHold: (held) => {
      isHolded.value = held;
    },
    onRegistered: () => {
      fetchQueue()
    },
    onCallReceived: () => {
      callRecieved.value = {
        callingPartyNumber:
        simpleUser.session.incomingInviteRequest.message.from.uri.normal.user,
      };
      if (
        presavedCaller.value ===
        simpleUser.session.incomingInviteRequest.message.from.uri.normal.user ||
        simpleUser.session.incomingInviteRequest.message.from.uri.normal.user === "ivr"
      ) {
        answerCall();
      } else {
        toast({
          component: ToastificationContent,
          props: {
            title:
              "Nueva Llamada Entrante de " +
              simpleUser.session.incomingInviteRequest.message.from.uri.normal.user,
            icon: "PhoneCallIcon",
            variant: "success",
          },
        });
        store.dispatch("sip/setIsRinging", true);
        isRingingLocal.value = true;
        store.dispatch("sip/setIconColor", "green");
      }
      store
        .dispatch("pbx_device/getCallerName", simpleUser.session.incomingInviteRequest.message.from.uri.normal.user)
        .then((response) => {
          store.dispatch("sip/setDeviceCaller", response);
        })
        .catch((e) => {
          toast(
            {
              component: ToastificationContent,
              props: {
                title: "Ocurrio un error al obtener la información del dispositivo",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            },
            {
              timeout: 10000,
            }
          );
        });
    },
    onUnregistered: () => {
      store.dispatch("sip/setIconColor", "red");
      isConnected.value = "Unregistered";
    },
    onServerDisconnect: (error) => {
      store.dispatch("sip/setIconColor", "red");
      isConnected.value = error;
    },
    onInvite: async (invitation) => {
      // console.log("SDP recibido:", invitation.body);
    }
  };

  const createUser = (audioElement) => {
    return new Promise((resolve, reject) => {
      try {
        let userAgent = {
          // traceSIP: true,
          logLevel: "log",
          displayName: device.user,
          authorizationPassword: device.password,
          authorizationUsername: device.user,
          uri: new URI("sip", device.user, device.domain),
          logBuiltinEnabled: true,
          viaHost: "omniview.cloud",
          hackViaTcp: false,
          //   authorizationHa1: device.ha1
        };

        const simpleUserOptions = {
          delegate: simpleUserDelegate,
          media: {
            remote: {
              audio: audioElement,
            },
            // local: {
            //   audio: audioElementLocal,
            // },
          },
          userAgentOptions: userAgent,
        };

        simpleUser = new SimpleUser(device.websocket, simpleUserOptions);

        resolve(simpleUser);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Retry connection
  let maxAttempts = 3;
  let currentAttempts = 0;

  const connect = () => {
    simpleUser
      .connect()
      .then(() => {
        isConnected.value = "Connected";
        store.dispatch("sip/setIconColor", "white");
        store.dispatch("sip/setIsRegistered");

        simpleUser
          .register(
            {expires: 600},
            {
              // An example of how to get access to a SIP response message for custom handling
              requestDelegate: {
                onReject: (response) => {
                  isConnected.value = "Registration Failed";
                  // store.dispatch("sip/unsetIsRegisted");
                },
              },
            }
          )
          .then(() => {
            store.dispatch("device_interphone/subscribeToForceDisconnect", {
              device_id: device.user,
              domain: device.domain
            });
            currentAttempts = 0;
            //alert(`[${simpleUser.id}] registered.\n`);
          })
          .catch((error) => {
            console.error(`[${simpleUser.id}] failed to register`);
            console.error(error);
            //alert(`[${simpleUser.id}] Failed to register.\n` + error);

            if (currentAttempts < maxAttempts) setTimeout(() => retryConnection(), 2000);
          });
      })
      .catch((error) => {
        console.error(`[${simpleUser.id}] failed to connect`);
        console.error(error);
        //alert("Failed to connect.\n" + error);

        if (currentAttempts < maxAttempts) setTimeout(() => retryConnection(), 2000);
      });
  };

  const retryConnection = () => {
    currentAttempts++;
    store.dispatch("sip/setIconColor", "green");
    connect();
  }

  const unregister = () => {
    simpleUser
      .unregister()
      .then(() => {
        // console.log("Entro a UNREGISTER");
      })
      .catch((error) => {
        console.error(`[${simpleUser.id}] failed to unregister`);
        console.error(error);
        //alert(`[${simpleUser.id}] Failed to unregister.\n` + error);
      });
  };

  const hangUp = () => {
    simpleUser.hangup().catch((error) => {
      console.error(`[${simpleUser.id}] failed to hangup call`);
      console.error(error);
      //alert("Failed to hangup call.\n" + error);
    });
  };

  const takeCall = (sessionId, caller, alarm) => {
    sessionID.value = sessionId
    lockAnswerCall.value = true;
    store
      .dispatch("pbx_calls/takeCall", {
        domain: device.domain,
        call_uuid: sessionId,
        extension: device.user
      })
      .then((response) => {
        store.dispatch("sip/setPresavedCaller", caller);
        toast({
          component: ToastificationContent,
          props: {
            title: response.message,
            icon: "CheckIcon",
            variant: "success",
          },
        });
      })
      .catch((e) => {
        console.error({e});
        toast(
          {
            component: ToastificationContent,
            props: {
              title: response.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          },
          {
            timeout: 10000,
          }
        );
      })
      .finally(() => {
        lockAnswerCall.value = false;
        // props.disableEnableButtons(false);
        // props.closeLoading();
      });
  };

  const answerCall = () => {
    simpleUser.answer().catch((error) => {
      console.error(`[${simpleUser.id}] failed to answer call`);
      console.error(error);
      alert(`[${simpleUser.id}] Failed to answer call.\n` + error);
    });
  };

  const transferCall = () => {
    if (transferUser.value != "") {
      const target = new URI("sip", transferUser.value, device.domain);
      //TODO: make reactive variable
      let transferSession = simpleUser.session;
      simpleUser.hold();
      //Verificar si es .call y este valida si ya hay una sessión activa o .sendInvite por que este es privado
      simpleUser.sendInvite(target);
      simpleUser.session.refer(transferSession);
    }
    transferUser.value = "";
  };

  const holdSession = () => {
    simpleUser.hold();
  };

  const unHoldSession = () => {
    simpleUser.unhold();
  };

  const makeCall = (destination) => {
    if (isRinging.value || isOnCall.value) {
      toast({
        component: ToastificationContent,
        props: {
          title: "Ya estas en una llamada",
          icon: "PhoneCallIcon",
          variant: "Danger",
        },
      });
      return;
    }

    store.dispatch("sip/setIsCalling", true);
    store.dispatch("sip/setIconColor", "yellow");

    let setupCall = {};

    simpleUser.call(destination, setupCall).catch((error) => {
      console.error(`[${simpleUser.id}] failed tocall`);
      store.dispatch("sip/setIsCalling", false);

      store.dispatch("sip/setIconColor", "white");
      hangUp();
    });
  };

  const makeCallWithPresavedCall = (data) => {
    if (isRinging.value || isOnCall.value) {
      toast({
        component: ToastificationContent,
        props: {
          title: "Ya estas en una llamada",
          icon: "PhoneCallIcon",
          variant: "Danger",
        },
      });
      return;
    }

    store
      .dispatch("sip/setPresavedCall", {
        callingPartyNumber: simpleUser.id,
        remotePartyNumber: data.callingPartyNumber,
        id_alarm: data.id_alarm,
      })
      .then((response) => {
        makeCall("sip:" + data.callingPartyNumber + "@" + domain.value);
      })
      .catch((e) => {
        console.error({e});
        this.showMessage("", e.message, "danger");
      })
      .finally(() => {});
  };

  const mute = () => {
    if (!isMuted.value) {
      isMuted.value = true;
      simpleUser.mute();
    } else {
      isMuted.value = false;
      simpleUser.unmute();
    }
  };

  const setUpSIP = () => {
    let audioElementAgain = document.getElementById("remoteAudio");
    let audioElementLocal = document.getElementById("localAudio");

    createUser(audioElementAgain, audioElementLocal)
      .then((simpleUser) => {
        connect();
      })
      .catch((e) => console.error(e));
  };

  const setUpInterval = () => {
    if (intervalId === null) {
      intervalId = setInterval(() => {
        if (!isOnCall.value) {
          if (!isRinging.value) {
            if (intervalRefresh.value) connect();
          }
        }
      }, 180000); //Cada 3.3 min
    }
  };

  const clearSetUpInterval = () => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const forceConnectSIP = () => {
    axios.post("/v1/device_interphone/force_disconnect", {
      device_id: device.user,
      domain: device.domain
    });
    shouldDisconnect.value = false;
    setTimeout(() => {
      shouldDisconnect.value = true;
    }, 25000); //25 segundos para que se limpie bandera.
    setUpSIP();
    store.dispatch("sip/getAll");
    setUpInterval();
  };

  const disconnectSIP = () => {
    if (simpleUser) {
      let options = {
        requestOptions: {
          extraHeaders: ["Expires: 0"],
        },
      };

      simpleUser
        .unregister(options)
        .then((r) => {
          // console.log(r);
        })
        .catch((e) => {
          console.error(e);
        });
      simpleUser.disconnect();
    }
    intervalRefresh.value = false;
    clearSetUpInterval();
    store.dispatch("device_interphone/unSubscribeToForceDisconnect", {
      device_id: device.user,
      domain: device.domain
    });
  };

  const chronometer = () => {
    let time = timeCall.value + 1;
    store.dispatch("sip/setTimeCall", time);
  };

  const startCronometer = () => {
    cronometerRef.value = setInterval(chronometer, 1000);
  };

  const stopCronometer = () => {
    cronometerRef.value = clearInterval(cronometerRef.value);
    store.dispatch("sip/setTimeCall", 0);
  };

  EventBus.on(`answer-call`, (params) => {
    if (isRingingLocal.value) {
      answerCall();
    }
  });

  EventBus.on(`take-call`, (params) => {
    if (params.provider === "omnicloud") takeCall(params.sessionId, params.caller, params.alarm)
  });

  EventBus.on(`hang-up`, (params) => {
    stopCronometer();
    hangUp();
  });

  return {
    isOnCall,
    isRinging,
    isCalling,
    isConnected,
    isMuted,
    isHolded,

    createUser,
    disconnectSIP,
    connect,
    makeCall,
    makeCallWithPresavedCall,
    mute,
    hangUp,
    transferCall,
    holdSession,
    unHoldSession,
    forceConnectSIP,
    setUpInterval,
    startCronometer,
    stopCronometer,
    shouldDisconnect
  };
}
